import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { env } from '@/config/env';
import { selectAccessToken, sessionExpired, type StateWithSession } from '@/shared/session';
import { SESSION_RENEWAL_ERROR, sessionRenewalMessage } from './problemDetails';
import { lastRefreshFailure, refreshSession } from './sessionRefresh';

/**
 * Query-string serializer: arrays become repeated keys (`filter=a&filter=b`, as ASP.NET binds
 * `string[]`). Only primitives are sent; undefined/null values are omitted.
 */
export function serializeParams(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    const values: unknown[] = Array.isArray(value) ? value : [value];
    for (const item of values) {
      if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
        search.append(key, String(item));
      }
    }
  }
  return search.toString();
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: env.apiBaseUrl,
  timeout: env.apiTimeoutMs,
  paramsSerializer: serializeParams,
  // Parse JSON by content type; anything else (e.g. plain-text status pages) stays text.
  responseHandler: 'content-type',
  // RTK's default only recognises application/json — also accept application/problem+json
  // (and other +json types) so ASP.NET Problem Details reach the UI as objects.
  isJsonContentType: (headers) =>
    /^application\/([\w.-]+\+)?json\b/i.test(headers.get('Content-Type') ?? ''),
  prepareHeaders: (headers, { getState }) => {
    const token = selectAccessToken(getState() as StateWithSession);
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    headers.set('Accept', 'application/json, application/problem+json');
    return headers;
  },
});

/**
 * Base query for every endpoint: attaches the bearer token. When the API rejects it, the session is
 * renewed once with the refresh cookie and the request retried; it ends only when that fails too.
 */
export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const usedToken = selectAccessToken(api.getState() as StateWithSession);
  let result = await rawBaseQuery(args, api, extraOptions);

  // A 401 for an anonymous call (e.g. wrong password on sign-in) is not a session problem.
  if (result.error?.status !== 401 || usedToken === null) return result;

  // Another request may already have renewed the token while this one was under way.
  const renewed = selectAccessToken(api.getState() as StateWithSession) !== usedToken;
  const outcome = renewed ? 'refreshed' : await refreshSession(api);
  if (outcome === 'refreshed') {
    result = await rawBaseQuery(args, api, extraOptions);
    if (result.error?.status === 401) api.dispatch(sessionExpired());
  }
  // `ended`: refreshSession ended the session. `unavailable`: keep it; this request just failed,
  // and the page says why the session could not be renewed instead of a bare 401.
  if (outcome === 'unavailable') {
    const refreshStatus = lastRefreshFailure()?.status ?? null;
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: sessionRenewalMessage(refreshStatus),
        data: { kind: SESSION_RENEWAL_ERROR, refreshStatus },
      },
      meta: result.meta,
    };
  }
  return result;
};
